import mergeAllOf from '@stoplight/json-schema-merge-allof';
import { isSchemaObject, ReferenceObject, SchemaObject } from 'openapi3-ts';

type SchemaOrReference = SchemaObject | ReferenceObject;

// `const` is JSON Schema / OpenAPI 3.1 and missing from openapi3-ts' types.
type SchemaWithConst = SchemaObject & { const?: unknown };

/**
 * `oneOf` and `anyOf` both draw a value's shape from a list of alternatives, so
 * only the rendered wording distinguishes them.
 */
const ALTERNATIVE_KEYWORDS = ['oneOf', 'anyOf'] as const;

type AlternativeKeyword = (typeof ALTERNATIVE_KEYWORDS)[number];

const isHiddenFromDocs = (schema: SchemaOrReference): boolean =>
  isSchemaObject(schema) &&
  (schema as SchemaObject & { 'x-hidden-from-docs'?: boolean })[
    'x-hidden-from-docs'
  ] === true;

/**
 * The alternatives the reference shows. A branch marked `x-hidden-from-docs`
 * stays in the published schema and the client libraries but not on the page,
 * the same contract the tag has on a field.
 */
const getVisibleAlternatives = (
  schema: SchemaObject | undefined,
  keyword: AlternativeKeyword,
): SchemaOrReference[] =>
  ((schema?.[keyword] as SchemaOrReference[] | undefined) || []).filter(
    (alternative) => !isHiddenFromDocs(alternative),
  );

const getAlternativeKeywords = (schema?: SchemaObject): AlternativeKeyword[] =>
  ALTERNATIVE_KEYWORDS.filter(
    (keyword) => getVisibleAlternatives(schema, keyword).length > 0,
  );

const getAlternativeOptions = (schema?: SchemaObject): SchemaOrReference[] =>
  getAlternativeKeywords(schema).flatMap((keyword) =>
    getVisibleAlternatives(schema, keyword),
  );

const hasAlternatives = (schema?: SchemaObject): boolean =>
  getAlternativeOptions(schema).length > 0;

const getOptionProperties = (
  option: SchemaOrReference,
): Record<string, SchemaOrReference> =>
  isSchemaObject(option)
    ? (option.properties as Record<string, SchemaOrReference>) || {}
    : {};

/** Property names declared by every alternative. */
const getSharedPropertyNames = (options: SchemaOrReference[]): string[] => {
  const [firstOptionNames, ...remainingOptionNames] = options.map((option) =>
    Object.keys(getOptionProperties(option)),
  );

  return (firstOptionNames || []).filter((propertyName) =>
    remainingOptionNames.every((propertyNames) =>
      propertyNames.includes(propertyName),
    ),
  );
};

const formatSchemaValue = (value: unknown): string =>
  typeof value === 'string' ? value : JSON.stringify(value);

/**
 * The single value a schema permits, when it permits exactly one — how a spec
 * marks which alternative a payload is.
 */
const getSingleSchemaValue = (
  schema?: SchemaOrReference,
): string | undefined => {
  if (schema == null || !isSchemaObject(schema)) {
    return undefined;
  }

  const { const: constValue } = schema as SchemaWithConst;
  if (constValue !== undefined) {
    return formatSchemaValue(constValue);
  }

  return schema.enum?.length === 1
    ? formatSchemaValue(schema.enum[0])
    : undefined;
};

// Keywords that restrict or annotate a field's values without making it a
// different schema. Alternatives differing only in these render as one row.
const VALUE_CONSTRAINT_KEYS = new Set([
  'const',
  'default',
  'deprecated',
  'description',
  'enum',
  'example',
  'examples',
  'readOnly',
  'writeOnly',
]);

// The subset with no values to combine, so one alternative's cannot stand in
// for the rest.
const ANNOTATION_KEYS = [
  'default',
  'deprecated',
  'description',
  'example',
  'examples',
  'readOnly',
  'writeOnly',
];

// Resolved references make a recursive schema a cycle of real objects, so every
// walk over one tracks what it is inside of.
const CYCLE = '[circular]';

// Key order is not meaningful in a schema, so compare as sorted key/value
// pairs rather than raw `JSON.stringify` output.
const canonicalizeForComparison = (
  value: unknown,
  ancestors: WeakSet<object> = new WeakSet(),
): unknown => {
  if (value == null || typeof value !== 'object') {
    return value;
  }
  if (ancestors.has(value)) {
    return CYCLE;
  }

  ancestors.add(value);
  const canonicalized = Array.isArray(value)
    ? value.map((element) => canonicalizeForComparison(element, ancestors))
    : Object.keys(value as Record<string, unknown>)
        .sort()
        .map((key) => [
          key,
          canonicalizeForComparison(
            (value as Record<string, unknown>)[key],
            ancestors,
          ),
        ]);
  // Only an ancestor is a cycle: one schema object appears in many places once
  // references are resolved, and each occurrence compares by its contents.
  ancestors.delete(value);

  return canonicalized;
};

const getSchemaSignature = (schema: SchemaOrReference): string =>
  JSON.stringify(canonicalizeForComparison(schema));

const getStructuralSignature = (schema: SchemaOrReference): string =>
  getSchemaSignature(
    Object.fromEntries(
      Object.entries(schema).filter(([key]) => !VALUE_CONSTRAINT_KEYS.has(key)),
    ),
  );

/**
 * Properties more than one alternative declares with a structurally different
 * schema, which cannot render as one definition without dropping the rest.
 */
const getDivergentPropertyNames = (schema?: SchemaObject): string[] => {
  const declarationsByName = new Map<string, SchemaOrReference[]>();

  getAlternativeOptions(schema).forEach((option) => {
    Object.entries(getOptionProperties(option)).forEach(
      ([propertyName, declaration]) => {
        declarationsByName.set(propertyName, [
          ...(declarationsByName.get(propertyName) || []),
          declaration,
        ]);
      },
    );
  });

  return Array.from(declarationsByName.entries())
    .filter(
      ([, declarations]) =>
        new Set(declarations.map(getStructuralSignature)).size > 1,
    )
    .map(([propertyName]) => propertyName);
};

/**
 * The field whose value identifies which alternative applies: an explicit
 * `discriminator`, or the field every alternative pins to a distinct value.
 */
const getSelectorFieldName = (schema?: SchemaObject): string | undefined => {
  const declaredPropertyName = schema?.discriminator?.propertyName;
  if (declaredPropertyName != null) {
    return declaredPropertyName;
  }

  const options = getAlternativeOptions(schema);
  if (options.length < 2) {
    return undefined;
  }

  return getSharedPropertyNames(options).find((propertyName) => {
    const selectorValues = options.map((option) =>
      getSingleSchemaValue(getOptionProperties(option)[propertyName]),
    );

    return (
      selectorValues.every((value) => value !== undefined) &&
      new Set(selectorValues).size > 1
    );
  });
};

/**
 * The value of the selecting field that picks this alternative. Only an
 * alternative pinning the field identifies itself: `discriminator.mapping` keys
 * values to references, and references are resolved before rendering.
 */
const getSelectorValue = (
  option: SchemaOrReference,
  selectorFieldName?: string,
): string | undefined =>
  selectorFieldName == null
    ? undefined
    : getSingleSchemaValue(getOptionProperties(option)[selectorFieldName]);

const mergeRequiredProperties = (
  schemaRequired: string[] = [],
  alternatives: SchemaObject[],
): string[] => {
  if (alternatives.length === 0) {
    return schemaRequired;
  }

  const [firstAlternative, ...remainingAlternatives] = alternatives;
  const requiredByEveryAlternative = (firstAlternative.required || []).filter(
    (property) =>
      remainingAlternatives.every((alternative) =>
        alternative.required?.includes(property),
      ),
  );

  return Array.from(
    new Set([...schemaRequired, ...requiredByEveryAlternative]),
  );
};

/**
 * Keeps every definition of a field its alternatives declare differently. They
 * merge into one row when the containing union says which value selects which;
 * otherwise the field keeps its alternatives so its own row can explain them.
 */
const buildDivergentProperty = (
  declarations: SchemaOrReference[],
  isExplainedByContainingUnion: boolean,
  // Carried from the containing union: whether choosing between the field's
  // definitions is exclusive is the same question as it was one level up.
  keyword: AlternativeKeyword,
  ancestors: WeakSet<object>,
): SchemaObject => {
  const signatures = declarations.map(getSchemaSignature);
  const variants = declarations.filter(
    (declaration, index) => signatures.indexOf(signatures[index]) === index,
  );

  // Only detail every alternative agrees on: one alternative's description
  // presented as the field's own is wrong whenever another applies.
  const descriptions = new Set(
    variants.map((variant) =>
      isSchemaObject(variant) ? variant.description : undefined,
    ),
  );
  const [onlyDescription] = Array.from(descriptions);
  const description = descriptions.size === 1 ? onlyDescription : undefined;

  const { oneOf: alternatives, ...merged } = normalizeSchema(
    { oneOf: variants },
    ancestors,
  );

  // Alternatives carrying no fields, such as unresolved references, would
  // otherwise be traded for nothing.
  const isMerged =
    isExplainedByContainingUnion &&
    Object.keys(merged.properties || {}).length > 0;

  return {
    ...(description != null ? { description } : {}),
    ...merged,
    ...(isMerged ? {} : { [keyword]: alternatives }),
  };
};

/**
 * Converts composition keywords into the shape the table consumes: `allOf`
 * merged, `oneOf` / `anyOf` combined so every field an alternative may send
 * appears, required only where every alternative requires it.
 */
const normalizeSchema = (
  schema: SchemaObject,
  ancestors: WeakSet<object>,
): SchemaObject => {
  // A recursive schema contains itself, so stop rather than descend forever.
  // The row for the repeated schema normalizes on its own when it renders.
  if (ancestors.has(schema)) {
    return schema;
  }
  ancestors.add(schema);

  let normalizedSchema =
    schema.allOf != null
      ? mergeAllOf(schema, {
          resolvers: {
            defaultResolver: mergeAllOf.options.resolvers.title,
          },
        })
      : schema;

  const normalizedAlternativeKeywords = Object.fromEntries(
    getAlternativeKeywords(normalizedSchema).map((keyword) => [
      keyword,
      getVisibleAlternatives(normalizedSchema, keyword).map((alternative) =>
        isSchemaObject(alternative)
          ? normalizeSchema(alternative, ancestors)
          : alternative,
      ),
    ]),
  );
  const alternatives = Object.values(normalizedAlternativeKeywords)
    .flat()
    .filter(isSchemaObject);

  const selectorFieldName = getSelectorFieldName({
    ...normalizedSchema,
    ...normalizedAlternativeKeywords,
  });
  const alternativeKeyword: AlternativeKeyword = Object.keys(
    normalizedAlternativeKeywords,
  ).includes('oneOf')
    ? 'oneOf'
    : 'anyOf';

  const baseProperties = normalizedSchema.properties || {};
  const properties = { ...baseProperties };
  const alternativePropertyNames = Array.from(
    new Set<string>(
      alternatives.flatMap((alternative) =>
        Object.keys(alternative.properties || {}),
      ),
    ),
  );

  alternativePropertyNames.forEach((propertyName) => {
    const baseProperty = baseProperties[propertyName];
    const alternativeProperties = alternatives
      .map((alternative) => alternative.properties?.[propertyName])
      .filter((alternativeProperty) => alternativeProperty != null);

    // A declaration on the schema itself describes the field for every
    // alternative, so it wins; alternatives only narrow its accepted values.
    if (
      baseProperty == null &&
      new Set(alternativeProperties.map(getStructuralSignature)).size > 1
    ) {
      properties[propertyName] = buildDivergentProperty(
        alternativeProperties,
        selectorFieldName != null,
        alternativeKeyword,
        ancestors,
      );
      return;
    }

    const displayedProperty = baseProperty || alternativeProperties[0];

    if (displayedProperty == null || !isSchemaObject(displayedProperty)) {
      properties[propertyName] = displayedProperty;
      return;
    }

    // `const` pins a field to one value, which is the same statement as a
    // one-value `enum` and is how many specs mark which alternative applies.
    const alternativeEnums = alternativeProperties
      .filter(isSchemaObject)
      .map((property) => {
        const { const: pinnedValue } = property as SchemaWithConst;
        return (
          property.enum ??
          (pinnedValue === undefined ? undefined : [pinnedValue])
        );
      });
    const everyAlternativeConstrainsTheField =
      alternativeProperties.length === alternatives.length &&
      alternativeEnums.every((enumValues) => enumValues != null);
    const everyDeclarationConstrainsTheField =
      alternativeEnums.length === alternativeProperties.length &&
      alternativeEnums.every((enumValues) => enumValues != null);
    const alternativeEnumValues = Array.from(
      new Set(alternativeEnums.flatMap((enumValues) => enumValues || [])),
    );
    const baseEnumValues =
      baseProperty != null &&
      isSchemaObject(baseProperty) &&
      baseProperty.enum != null
        ? baseProperty.enum
        : undefined;
    const enumValues =
      baseEnumValues != null
        ? everyAlternativeConstrainsTheField
          ? baseEnumValues.filter((value) =>
              alternativeEnumValues.includes(value),
            )
          : baseEnumValues
        : everyDeclarationConstrainsTheField
        ? alternativeEnumValues
        : undefined;
    const {
      enum: _displayedEnum,
      const: _displayedConst,
      ...displayedPropertyWithoutValues
    } = displayedProperty as SchemaWithConst;

    // What the alternatives disagree on cannot be stated as the field's own. A
    // declaration on the schema itself is exempt: it describes them all.
    const disagreedKeys =
      baseProperty != null
        ? []
        : ANNOTATION_KEYS.filter((key) => {
            const declared = alternativeProperties
              .filter(isSchemaObject)
              .map((property) => getSchemaSignature(property[key] ?? null));
            return new Set(declared).size > 1;
          });

    properties[propertyName] = Object.fromEntries([
      ...Object.entries(displayedPropertyWithoutValues).filter(
        ([key]) => !disagreedKeys.includes(key),
      ),
      ...(enumValues == null ? [] : [['enum', enumValues]]),
    ]);
  });

  const items =
    normalizedSchema.items != null && isSchemaObject(normalizedSchema.items)
      ? normalizeSchema(normalizedSchema.items, ancestors)
      : normalizedSchema.items;

  const alternativeTypes = Array.from(
    new Set(
      alternatives.map((alternative) => alternative.type).filter(Boolean),
    ),
  );

  normalizedSchema = {
    ...normalizedSchema,
    ...normalizedAlternativeKeywords,
    ...(Object.keys(properties).length > 0 ? { properties } : {}),
    ...(items != null ? { items } : {}),
    ...(normalizedSchema.type == null && alternativeTypes.length === 1
      ? { type: alternativeTypes[0] }
      : {}),
  };

  const required = mergeRequiredProperties(
    normalizedSchema.required,
    alternatives,
  );
  ancestors.delete(schema);

  return required.length > 0
    ? { ...normalizedSchema, required }
    : normalizedSchema;
};

// The table asks for the same schemas repeatedly — per row, per depth level,
// per render — and schema objects come from the parsed spec and never change.
// Only whole normalizations are cached: a result from inside a cycle describes
// that position, not the schema.
const normalizedSchemas = new WeakMap<object, SchemaObject>();

const normalizeSchemaForRendering = (schema: SchemaObject): SchemaObject => {
  if (schema == null || typeof schema !== 'object') {
    return schema;
  }

  const cached = normalizedSchemas.get(schema);
  if (cached != null) {
    return cached;
  }

  const normalized = normalizeSchema(schema, new WeakSet());
  normalizedSchemas.set(schema, normalized);
  return normalized;
};

/**
 * Whether a normalized schema has anything to show when expanded. The row
 * toggle and the table's expand-all control share it, so the control cannot
 * offer to expand rows with nothing to open.
 */
const isExpandable = (schemaItem: SchemaObject): boolean => {
  if (schemaItem.properties != null || hasAlternatives(schemaItem)) {
    return true;
  }

  const arrayMembers: SchemaObject = schemaItem.items;
  return (
    arrayMembers != null &&
    (arrayMembers.type === 'object' ||
      arrayMembers.properties != null ||
      hasAlternatives(arrayMembers))
  );
};

// Decreases the left margin for objects that are deeply nested
const ADJUST_INDENT_AFTER_DEPTH = 2;
const DEFAULT_INDENT = 2.0;
const MINIMUM_INDENT = 0.5;
const SHRINK_INCREMENT = 0.4;
const UNIT_MULTIPLIER_FROM_CSS_VAR = 0.8;

/**
 * Determines just how much we should be indenting each schema row based on its
 * depth level and the overall depth of the object. This lets us subtly
 * decrease the indent size for very large objects
 *
 *
 * @param currentDepth The current depth of the schema row
 * @param maximumDepth The maximum depth of the schema object, as determined by calling "getSchemaDepth"
 * @returns The amount, in rems, that we should
 */
const getIndentLevel = (currentDepth: number, maximumDepth: number) => {
  const indentInRems =
    maximumDepth > ADJUST_INDENT_AFTER_DEPTH && currentDepth > 0
      ? Math.max(
          MINIMUM_INDENT,
          DEFAULT_INDENT -
            SHRINK_INCREMENT * (maximumDepth - ADJUST_INDENT_AFTER_DEPTH),
        )
      : DEFAULT_INDENT;
  return indentInRems * UNIT_MULTIPLIER_FROM_CSS_VAR;
};

// Cached for the same reason: the table measures every visible entry on each
// render, and each measurement walks that entry's whole tree.
const schemaDepths = new WeakMap<object, number>();

/**
 * A utility function that analyzes our schema objects to find out just how
 * deeply nested they are -- primarily used to determine how aggressive we are
 * with our indents.
 *
 * @param schemaItem The schema request or response object we're analyzing
 * @returns How deep the schema object is nested
 */
const getSchemaDepth = (schemaItem: SchemaObject): number => {
  const cachedDepth =
    schemaItem != null && typeof schemaItem === 'object'
      ? schemaDepths.get(schemaItem)
      : undefined;
  if (cachedDepth !== undefined) {
    return cachedDepth;
  }

  const normalizedSchemaItem = normalizeSchemaForRendering(schemaItem);
  let maxSoFar = 0;

  if (normalizedSchemaItem.properties != null) {
    maxSoFar = Math.max(
      maxSoFar,
      ...Object.values(normalizedSchemaItem.properties).map(
        (nextVal) => 1 + getSchemaDepth(nextVal),
      ),
    );
  } else {
    const schemaItemSubItems: SchemaObject = normalizedSchemaItem.items;
    if (schemaItemSubItems != null && schemaItemSubItems.properties != null) {
      maxSoFar = Math.max(
        maxSoFar,
        ...Object.values(schemaItemSubItems.properties).map(
          (nextVal) => 1 + getSchemaDepth(nextVal),
        ),
      );
    }
  }

  if (schemaItem != null && typeof schemaItem === 'object') {
    schemaDepths.set(schemaItem, maxSoFar);
  }
  return maxSoFar;
};

export type { SchemaOrReference };
export {
  formatSchemaValue,
  getAlternativeKeywords,
  getAlternativeOptions,
  getDivergentPropertyNames,
  getIndentLevel,
  getOptionProperties,
  getSchemaDepth,
  getSelectorFieldName,
  getSelectorValue,
  getSharedPropertyNames,
  getSingleSchemaValue,
  hasAlternatives,
  isExpandable,
  normalizeSchemaForRendering,
};
